/**
 * Particle occlusion: particles hide behind models and fade out behind splat surfaces.
 *
 * Particles draw right after the splats (Particles layer, transparent sub-layer inserted after
 * TranslucentEntityLayer) in the same pass and depth buffer as the opaque models, so the hardware
 * depth test hides them behind models. The splats write no depth, so behind a splat surface each
 * particle fragment is faded with the splat depth map (SplatDepthPass), exactly like translucent
 * entities (utils/materialOpacity.ts), over a slightly wider soft range.
 *
 * Why a device-level chunk and not material.getShaderChunks(): ParticleMaterial.getShaderVariant
 * (engine 2.21.2 and 2.23.2) never passes the material's chunks to ShaderGeneratorParticle, which
 * builds from ShaderChunks.get(device, language) only, and the emitter recreates its material on
 * every rebuild anyway. So we patch `particlePS` in the device's chunk map once, right after the
 * app is created (the ParticleSystemComponentSystem registers the particle chunks in its
 * constructor) and before any emitter compiles. The particle program cache key does not include
 * chunk text, so patching later would not reach already-compiled variants.
 *
 * Hook: the engine's particlePS is the module-scope declarations plus the start of main(), and it
 * ends right after `a` (the particle alpha) is computed — in GLSL and WGSL, in 2.21.2 and 2.23.2.
 * We prepend our declarations and append `a *= particleSplatVisibility();`, so it runs before
 * particle_softPS, the lighting chunks and the blend chunks (blendAdd multiplies rgb by `a`,
 * blendMultiply mixes rgb towards 1 by `a`, blendNormal / ADDITIVEALPHA blend with `a`), which
 * makes one hook correct for every blend mode, and keeps the blend chunks' discards.
 * The engine text is read at runtime, never copied (2.21.2's WGSL particlePS still declares
 * saturate(); 2.23.2's does not), and a shape check skips the patch if the chunk changes.
 */
import * as pc from 'playcanvas';

const MARKER = 'STORYSPLAT_PARTICLE_SPLAT_DEPTH';
export const PARTICLE_CHUNK = 'particlePS';

/**
 * Soft range multiplier over the translucent entities' range (uSplatDepthParams.y/z, 2% of depth,
 * min 0.02): particles fade over ~6% of their depth, centred on the splat surface.
 */
export const PARTICLE_SPLAT_SOFT_SCALE = 3.0;

export const PARTICLE_SPLAT_DECLS_GLSL = /* glsl */ `
// ${MARKER}
uniform vec4 uSplatDepthParams;
uniform highp sampler2D uSplatDepthMap;
#ifndef VIEWPORT_SIZE
    #define VIEWPORT_SIZE
    uniform vec4 viewport_size;
#endif
#ifndef CAMERAPLANES
    #define CAMERAPLANES
    uniform vec4 camera_params;
#endif
const float PARTICLE_SPLAT_SOFT_SCALE = ${PARTICLE_SPLAT_SOFT_SCALE.toFixed(4)};
float particleSplatDepthAt(vec2 uv) {
    ivec2 size = textureSize(uSplatDepthMap, 0);
    ivec2 texel = clamp(ivec2(uv * vec2(size)), ivec2(0), size - 1);
    vec4 data = texelFetch(uSplatDepthMap, texel, 0);
    #ifdef CAPS_TEXTURE_FLOAT_RENDERABLE
        return data.r;
    #else
        // RGBA8 target: the engine prepass packs the float's bits (floatAsUintPS float2uint)
        uint bits = (uint(data.r * 255.0) << 24u) | (uint(data.g * 255.0) << 16u) | (uint(data.b * 255.0) << 8u) | uint(data.a * 255.0);
        return uintBitsToFloat(bits);
    #endif
}
float particleSplatVisibility() {
    // x: the map was rendered this frame; otherwise no splat test (hardware depth still hides models)
    if (uSplatDepthParams.x < 0.5) return 1.0;
    // viewport_size is updated per render target (CameraFrame render scale included)
    float splatDepth = particleSplatDepthAt(gl_FragCoord.xy * viewport_size.zw);
    float fragDepth = camera_params.w == 0.0
        ? 1.0 / gl_FragCoord.w
        : camera_params.z + gl_FragCoord.z * (camera_params.y - camera_params.z);
    float soft = max(uSplatDepthParams.z, fragDepth * uSplatDepthParams.y) * PARTICLE_SPLAT_SOFT_SCALE;
    return clamp((splatDepth - fragDepth) / soft + 0.5, 0.0, 1.0);
}
`;

export const PARTICLE_SPLAT_FADE_GLSL = /* glsl */ `
    a *= particleSplatVisibility();
`;

export const PARTICLE_SPLAT_DECLS_WGSL = /* wgsl */ `
// ${MARKER}
uniform uSplatDepthParams: vec4f;
var uSplatDepthMap: texture_2d<uff>;
#ifndef VIEWPORT_SIZE
    #define VIEWPORT_SIZE
    uniform viewport_size: vec4f;
#endif
#ifndef CAMERAPLANES
    #define CAMERAPLANES
    uniform camera_params: vec4f;
#endif
const PARTICLE_SPLAT_SOFT_SCALE: f32 = ${PARTICLE_SPLAT_SOFT_SCALE.toFixed(4)};
fn particleSplatDepthAt(uv: vec2f) -> f32 {
    // WebGPU always renders R32F, so no RGBA8 unpacking here
    let size = vec2i(textureDimensions(uSplatDepthMap, 0));
    let texel = clamp(vec2i(uv * vec2f(size)), vec2i(0), size - vec2i(1));
    return textureLoad(uSplatDepthMap, texel, 0).r;
}
fn particleSplatVisibility() -> f32 {
    if (uniform.uSplatDepthParams.x < 0.5) {
        return 1.0;
    }
    let splatDepth = particleSplatDepthAt(pcPosition.xy * uniform.viewport_size.zw);
    var fragDepth: f32 = 1.0 / pcPosition.w;
    if (uniform.camera_params.w != 0.0) {
        fragDepth = uniform.camera_params.z + pcPosition.z * (uniform.camera_params.y - uniform.camera_params.z);
    }
    let soft = max(uniform.uSplatDepthParams.z, fragDepth * uniform.uSplatDepthParams.y) * PARTICLE_SPLAT_SOFT_SCALE;
    return clamp((splatDepth - fragDepth) / soft + 0.5, 0.0, 1.0);
}
`;

export const PARTICLE_SPLAT_FADE_WGSL = /* wgsl */ `
    a = a * particleSplatVisibility();
`;

// particlePS must end inside main(), right after the particle alpha is declared
const TAIL_GLSL = /float\s+a\s*=\s*tex\.a\s*\*\s*ramp\.a\s*;\s*$/;
const TAIL_WGSL = /var\s+a\s*:\s*f32\s*=\s*tex\.a\s*\*\s*ramp\.a\s*;\s*$/;

/** The engine's particlePS with the splat fade; the input unchanged if already patched; null if the chunk changed shape. */
export function patchParticlePSGLSL(base: string): string | null {
    if (base.includes(MARKER)) return base;
    if (!TAIL_GLSL.test(base)) return null;
    return PARTICLE_SPLAT_DECLS_GLSL + base + PARTICLE_SPLAT_FADE_GLSL;
}
export function patchParticlePSWGSL(base: string): string | null {
    if (base.includes(MARKER)) return base;
    if (!TAIL_WGSL.test(base)) return null;
    return PARTICLE_SPLAT_DECLS_WGSL + base + PARTICLE_SPLAT_FADE_WGSL;
}

/**
 * Patch the device's particlePS (both languages). Call once right after the app is created, before
 * any particle system exists. Returns false (and leaves the chunk alone) if the active language's
 * chunk changed shape. Idempotent.
 */
export function installParticleSplatDepth(device: pc.GraphicsDevice): boolean {
    const languages: Array<[string, (s: string) => string | null]> = [
        [pc.SHADERLANGUAGE_GLSL, patchParticlePSGLSL],
        [pc.SHADERLANGUAGE_WGSL, patchParticlePSWGSL],
    ];
    const active = device.isWebGPU ? pc.SHADERLANGUAGE_WGSL : pc.SHADERLANGUAGE_GLSL;
    let ok = true;
    for (const [language, patch] of languages) {
        const chunks = pc.ShaderChunks.get(device, language);
        const base = chunks.get(PARTICLE_CHUNK);
        const patched = base ? patch(base) : null;
        if (patched) {
            chunks.set(PARTICLE_CHUNK, patched);
        } else if (language === active) {
            ok = false;
        }
    }
    if (!ok) console.warn('[StorySplat Viewer] particle splat occlusion: the engine particle chunk changed, skipped');
    return ok;
}

interface CulledInstances {
    opaque: pc.MeshInstance[];
    transparent: pc.MeshInstance[];
}
type LayerWithCulling = pc.Layer & { getCulledInstances(camera: pc.Camera): CulledInstances | undefined };

/**
 * For SplatDepthPass `isNeeded` (evaluated in buildFrameGraph, BEFORE this frame's culling in
 * 2.21/2.23 renderComposition): an enabled particle system is in the layer and drawing. The
 * component removes a disabled entity's instance from its layers, and the emitter / component set
 * meshInstance.visible = false while paused (autoPlay off) or after a non-looping run ends.
 */
export function hasVisibleParticles(meshInstances: ReadonlyArray<{ visible: boolean }>): boolean {
    for (let i = 0; i < meshInstances.length; i++) if (meshInstances[i].visible) return true;
    return false;
}

/** For `alsoNeeded` (evaluated in SplatDepthPass.execute, AFTER culling): a particle system survived culling. */
export function particlesOnScreen(layer: pc.Layer, camera: pc.Camera): boolean {
    const culled = (layer as LayerWithCulling).getCulledInstances(camera);
    return !!culled && (culled.transparent.length > 0 || culled.opaque.length > 0);
}
